import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import Businesses from "../classes/Businesses";
import PageNavigator from "../classes/PageNavigator";
import BusinessStatus from "../classes/BusinessStatus";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import SearchComponent from "./SearchComponent";
import ListWrapper from "../utils/ListWrapper";
import BusinessesRequest from "../models/BusinessesRequest";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import Business from "../models/Business";
import Divider from "./Divider";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface AdminBusinessManagementPageProps extends BaseUIProps {
  key?: string;
}

class _AdminBusinessManagementPageState extends ObservableComponent<AdminBusinessManagementPageProps> {
  businessessList: Businesses = null;
  businesses: Array<Business> = ListWrapper.widget(this, "businesses");
  filterBusinesses: Array<Business> = ListWrapper.widget(
    this,
    "filterBusinesses"
  );
  request: BusinessesRequest = null;
  searchText: string = "";
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminBusinessManagementPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "request",
        "request.ascending",
        "request.name",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
      ],
      this.computeBusinessessList
    );

    this.computeBusinessessList();

    this.on(
      ["businessessList", "businessessList.items"],
      this.computeBusinesses
    );

    this.computeBusinesses();

    this.on(
      ["businesses", "businesses.name", "searchText"],
      this.computeFilterBusinesses
    );

    this.computeFilterBusinesses();

    this.on(
      [
        "businessessList",
        "businessessList.count",
        "businessessList.items",
        "filterBusinesses",
        "filterBusinesses.businessStatus",
        "filterBusinesses.createdDate",
        "filterBusinesses.details",
        "filterBusinesses.logo",
        "filterBusinesses.name",
        "filterBusinesses.providers",
        "filterBusinesses.providers.isManager",
        "filterBusinesses.providers.name",
        "request",
        "request.offset",
        "request.pageSize",
      ],
      this.rebuild
    );
  }
  public setBusinessessList(val: Businesses): void {
    let isValChanged: boolean = this.businessessList !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("businessessList", this.businessessList, val);

    MessageDispatch.get().dispose(this.businessessList);

    this.businessessList = val;

    this.fire("businessessList", this);
  }
  public computeBusinessessList = async (): Promise<void> => {
    try {
      this.setBusinessessList(
        await Query.get().getBusinesses(
          UsageConstants.QUERY_GETBUSINESSES_ADMINBUSINESSMANAGEMENTPAGE_PROPERTIES_BUSINESSESSLIST_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeBusinessessList : " + exception.toString()
      );

      this.setBusinessessList(null);
    }
  };
  public setBusinesses(val: Array<Business>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.businesses,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("businesses", this.businesses, val);

    this.businesses.clear();

    this.businesses.addAll(val);

    this.fire("businesses", this);
  }
  public addToBusinesses(val: Business, index: number = -1): void {
    if (index === -1) {
      if (!this.businesses.contains(val)) this.businesses.add(val);
    } else {
      this.businesses.remove(this.businesses.elementAt(index));

      this.businesses.add(val);
    }

    this.fire("businesses", this, val, true);

    this.updateObservable("businesses", null, val);
  }
  public removeFromBusinesses(val: Business): void {
    this.businesses.remove(val);

    this.fire("businesses", this, val, false);

    this.removeObservable("businesses", val);
  }
  public computeBusinesses = (): void => {
    try {
      this.setBusinesses(
        Array.from(
          this.businessessList.items.isNotEmpty
            ? this.businessessList.items
            : []
        )
      );
    } catch (exception) {
      console.log(" exception in computeBusinesses : " + exception.toString());

      this.setBusinesses([]);
    }
  };
  public setFilterBusinesses(val: Array<Business>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.filterBusinesses,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("filterBusinesses", this.filterBusinesses, val);

    this.filterBusinesses.clear();

    this.filterBusinesses.addAll(val);

    this.fire("filterBusinesses", this);
  }
  public addToFilterBusinesses(val: Business, index: number = -1): void {
    if (index === -1) {
      if (!this.filterBusinesses.contains(val)) this.filterBusinesses.add(val);
    } else {
      this.filterBusinesses.remove(this.filterBusinesses.elementAt(index));

      this.filterBusinesses.add(val);
    }

    this.fire("filterBusinesses", this, val, true);

    this.updateObservable("filterBusinesses", null, val);
  }
  public removeFromFilterBusinesses(val: Business): void {
    this.filterBusinesses.remove(val);

    this.fire("filterBusinesses", this, val, false);

    this.removeObservable("filterBusinesses", val);
  }
  public computeFilterBusinesses = (): void => {
    try {
      this.setFilterBusinesses(
        Array.from(
          this.businesses.where((c) =>
            c.name.toLowerCase().contains(this.searchText.toLowerCase())
          )
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeFilterBusinesses : " + exception.toString()
      );

      this.setFilterBusinesses([]);
    }
  };
  public setRequest(val: BusinessesRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setSearchText(val: string): void {
    let isValChanged: boolean = this.searchText !== val;

    if (!isValChanged) {
      return;
    }

    this.searchText = val;

    this.fire("searchText", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        TextView({
          data: "Business Management",
          style: new ui.TextStyle({
            fontSize: cStyle.tTextViewHeadlineTwoFontSizeOn,
            fontWeight: cStyle.tTextViewHeadlineTwoFontWeightOn,
          }),
          className: "x202 hc",
          key: "0",
        }),
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.zero,
                  width: 300,
                  child: SearchComponent({
                    placeholder: "Search",
                    onChanged: (value) => {
                      this.onSearchTextHandler(value);
                    },
                  }),
                  key: "0",
                  className: "x969 hc",
                }),
              ],
              className: "x95 hc h",
              key: "0",
            }),
            Divider({ className: "xb8 hc h", key: "1" }),
            ui.Column({
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  columnWidths: {
                    0: new ui.FlexColumnWidth(1),
                    1: new ui.FlexColumnWidth(1),
                    2: new ui.FlexColumnWidth(1),
                    3: new ui.FlexColumnWidth(1),
                    4: new ui.FlexColumnWidth(1),
                  },
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          child: TextView({
                            data: "Business",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "x20f hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Description",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "x6b6 hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Created Date",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "xb0f hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Owner",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "x67a hc",
                          }),
                          className: "hc",
                          key: "3",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Status",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "x0b2 hc",
                          }),
                          className: "hc",
                          key: "4",
                        }),
                      ],
                      className: "x70 hc",
                      key: "0",
                    }),
                    this.filterBusinesses.expand((bsItem) => [
                      ui.TableRow({
                        children: [
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: ui.Row({
                              children: [
                                bsItem.logo === null
                                  ? ui.ClipRRect({
                                      borderRadius: ui.BorderRadius.circular(4),
                                      child: ui.AssetImage({
                                        path: "images/Kitchen.jpg",
                                        width: 40,
                                        fit: ui.BoxFit.cover,
                                        height: 40,
                                        className: "xf7 hc vc",
                                      }),
                                    })
                                  : ui.ClipRRect({
                                      borderRadius: ui.BorderRadius.circular(4),
                                      child: ui.NetworkImage({
                                        url: bsItem.logo.downloadUrl,
                                        width: 40,
                                        fit: ui.BoxFit.cover,
                                        height: 40,
                                        className: "xeb hc vc",
                                      }),
                                    }),
                                TextView({
                                  data: bsItem.name,
                                  softWrap: true,
                                  style: new ui.TextStyle({
                                    fontSize:
                                      cStyle.tTextViewHeadlineSixFontSizeOn,
                                    fontWeight:
                                      cStyle.tTextViewHeadlineSixFontWeightOn,
                                  }),
                                  className: "x7df",
                                  key: "1",
                                }),
                              ],
                              className: "xa5 hc h",
                            }),
                            className: "x5ac hc h",
                            key: "0",
                          }),
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: TextView({
                              data: bsItem.details,
                              overflow: ui.TextOverflow.ellipsis,
                              className: "hc",
                            }),
                            className: "hc",
                            key: "1",
                          }),
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: TextView({
                              data: bsItem.createdDate.toString(),
                              className: "hc",
                            }),
                            className: "hc",
                            key: "2",
                          }),
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: TextView({
                              data: bsItem.providers
                                .where((p) => p.isManager)
                                .map((p) => p.name)
                                .join(",")
                                .toString(),
                              className: "hc",
                            }),
                            className: "hc",
                            key: "3",
                          }),
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: TextView({
                              data: bsItem.businessStatus.name,
                              className: "hc",
                            }),
                            className: "hc",
                            key: "4",
                          }),
                        ],
                        onTap: (e) => {
                          e.stopPropagation();

                          this.onTapHandler(bsItem);
                        },
                        className: "x0fd hc h",
                        key: bsItem?.ident,
                      }),
                    ]),
                  ],
                  className: "xc4 hc h",
                  key: "0",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.end,
                  children: [
                    PaginationView({
                      pageSize: this.request.pageSize,
                      total:
                        this.businessessList !== null
                          ? this.businessessList.count
                          : 0,
                      offset: this.request.offset,
                      states: ui.joinStates(
                        {
                          "data-visibility":
                            this.businessessList !== null &&
                            this.businessessList.items.isNotEmpty,
                        },
                        {}
                      ),
                      key: "0",
                    }),
                  ],
                  className: "x61 hc h",
                  key: "1",
                }),
              ],
              className: "xcc hc h",
              key: "2",
            }),
          ],
          className: "xb7d hc h",
          key: "1",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AdminBusinessManagementPage x55 hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new BusinessesRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "",
        ascending: true,
      })
    );
  };
  public onSearchTextHandler = (value: string): void => {
    this.setSearchText(value);
  };
  public onTapHandler = (bsItem: Business): void => {
    this.navigator.pushAdminBusinessDetailsPage({
      business: bsItem,
      target: "main",
      replace: false,
    });
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.businessessList);

    super.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
}
export default function AdminBusinessManagementPage(
  props: AdminBusinessManagementPageProps
) {
  return React.createElement(_AdminBusinessManagementPageState, props);
}
